import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { validateMetadata, type AssetDetail } from '@prism/contracts';
import { api, ApiError } from '../api/client';
import { useCategories, useSchema } from '../api/hooks';
import { Modal } from './Modal';
import { MetadataSections } from './MetadataFields';
import { toast } from './Toasts';
import { queryKeys } from '../api/queryKeys';

/** Create an empty asset (placeholder): title, class and metadata now, media later. */
export function NewPlaceholderDialog({ onClose }: { onClose: () => void }) {
  const schemaQ = useSchema();
  const categories = useCategories().data?.items ?? [];
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [categoryId, setCategoryId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const schema = schemaQ.data?.definition;

  async function submit() {
    if (!schema) return;
    const local = validateMetadata(schema, values, categoryId || null);
    if (!local.ok) { setErrors(local.errors); return; }
    const { f_title, ...rest } = local.value;
    setBusy(true);
    try {
      const created = await api<AssetDetail>('/api/v1/assets', { method: 'POST', json: { title: String(f_title), categoryId: categoryId || null, metadata: rest } });
      void qc.invalidateQueries({ queryKey: queryKeys.assets() });
      toast(`Created placeholder ${created.assetCode}`);
      onClose();
      navigate(`/assets/${created.id}`);
    } catch (err) {
      const e = err as ApiError;
      if (e.code === 'VALIDATION_FAILED') setErrors((e.details?.fields as Record<string, string>) ?? {});
      else toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal wide title="New placeholder" onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !schema} onClick={() => void submit()} data-testid="create-placeholder">
        {busy ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Create placeholder
      </button>
    </>}>
      <p className="muted" style={{ margin: 0 }}>A placeholder is an asset without media yet. It gets its asset ID now; upload a file into it later, or let a watch folder fill it from a sidecar that names the ID.</p>
      {!schema ? <div className="skeleton" style={{ height: 160 }} /> : (
        <form className="form" onSubmit={(e) => { e.preventDefault(); void submit(); }} data-testid="placeholder-form">
          <div className="field">
            <label htmlFor="ph-class">Class</label>
            <select id="ph-class" className="select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">No class</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <MetadataSections schema={schema} values={values} categoryId={categoryId || null} errors={errors} canEdit editable={schemaQ.data?.editable}
            onChange={(id, v) => { setValues((p) => ({ ...p, [id]: v })); setErrors((e) => { const n = { ...e }; delete n[id]; return n; }); }} />
        </form>
      )}
    </Modal>
  );
}
